/**
 * Hook for fetching and managing expired visitors
 * Expired visitors have visitor_state === 'expired' in their role metadata
 */

import { useQuery } from '@tanstack/react-query';
import { condoApi } from '../services/api';
import { getAbsoluteImageUrl } from '../utils/imageUrl';
import { logger } from '../services/logger';

export interface ExpiredVisitor {
  id: string;
  name: string;
  photo: string | null;
  unit: string;
  personId: string;
  roleId: string;
  roleType: 'visitor' | 'service_provider';
  statusType: 'expired' | 'exited';
  expiredAt: string;
  expiredDaysAgo: number;
  devicePersonId?: string; // For device removal
  devices?: string[]; // Device IDs where registered
}

interface RawPerson {
  id: string;
  first_name: string;
  last_name: string;
  /** Presigned S3 URL — expires 15 min after the API call. Prefer proxy URLs below. */
  identity_photo_url?: string | null;
  /** Non-expiring proxy URL: /api/v1/images/{bucket}/{key} */
  profile_picture_url?: string | null;
  /** Preloaded identity photo object — photo_url is a non-expiring proxy URL */
  identity_photo?: { photo_url?: string | null } | null;
  roles: Array<{
    id: string;
    role_type: string;
    is_active: boolean;
    unit?: {
      id: string;
      name: string;
    };
    role_metadata?: {
      visitor_state?: string;
      expiry_date?: string;
      device_person_id?: string;
      assigned_devices?: string[];
    };
  }>;
}

/**
 * Transform raw API person to ExpiredVisitor
 */
const transformToExpiredVisitor = (person: RawPerson): ExpiredVisitor | null => {
  // Find the expired/exited visitor/service_provider role
  const expiredRole = person.roles.find(
    r =>
      (r.role_type === 'visitor' || r.role_type === 'service_provider') &&
      (r.role_metadata?.visitor_state === 'expired' ||
        r.role_metadata?.visitor_state === 'exited')
  );

  if (!expiredRole) {
    return null;
  }

  const expiryDate = expiredRole.role_metadata?.expiry_date
    ? new Date(expiredRole.role_metadata.expiry_date)
    : new Date();

  const daysAgo = Math.floor((Date.now() - expiryDate.getTime()) / (1000 * 60 * 60 * 24));

  return {
    id: person.id,
    name: `${person.first_name} ${person.last_name}`.trim(),
    // Prefer non-expiring proxy URLs over presigned S3 URLs (presigned expire after 15 min,
    // causing broken images when React Query serves stale cached data).
    photo: getAbsoluteImageUrl(
      person.profile_picture_url ||
      person.identity_photo?.photo_url ||
      person.identity_photo_url
    ) || null,
    unit: expiredRole.unit?.name || 'N/A',
    personId: person.id,
    roleId: expiredRole.id,
    roleType: expiredRole.role_type as 'visitor' | 'service_provider',
    statusType: (expiredRole.role_metadata?.visitor_state === 'exited' ? 'exited' : 'expired') as 'expired' | 'exited',
    expiredAt: expiredRole.role_metadata?.expiry_date || new Date().toISOString(),
    expiredDaysAgo: daysAgo,
    devicePersonId: expiredRole.role_metadata?.device_person_id,
    devices: expiredRole.role_metadata?.assigned_devices,
  };
};

/**
 * Fetch the expired/exited visitor list for a customer.
 *
 * Plain async function, deliberately free of react-query, so non-UI consumers
 * (useExpiredVisitorAutoRevoke runs on a timer, not a render) can reuse the exact
 * same query + transform without dragging a QueryClient into their callers.
 */
export const fetchExpiredVisitors = async (customerId: string | null): Promise<ExpiredVisitor[]> => {
  if (!customerId) {
    return [];
  }

  logger.debug('useExpiredVisitors', 'Fetching expired visitors', { customerId });

  // Fetch visitors and service providers with inactive roles
  const response = await condoApi.getPeople(customerId, {
    role_type: 'visitor,service_provider',
    include_inactive_roles: true, // CRITICAL: includes expired visitors
    page_size: 100,
  });

  const people: RawPerson[] = response.data?.people || response.data?.data || [];

  // Filter and transform to expired/exited visitors
  const expiredVisitors = people
    .map(transformToExpiredVisitor)
    .filter((v): v is ExpiredVisitor => v !== null)
    .sort((a, b) => {
      // expired first, then exited
      if (a.statusType !== b.statusType) {
        return a.statusType === 'expired' ? -1 : 1;
      }
      // Within same status, most recently expired/exited first (smaller daysAgo = more recent)
      return a.expiredDaysAgo - b.expiredDaysAgo;
    });

  logger.info('useExpiredVisitors', 'Expired visitors loaded', {
    total: expiredVisitors.length,
    customerId,
  });

  return expiredVisitors;
};

/**
 * Hook to fetch expired visitors for a customer
 */
export const useExpiredVisitors = (customerId: string | null) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['expired-visitors', customerId],
    queryFn: () => fetchExpiredVisitors(customerId),
    enabled: !!customerId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  return {
    visitors: data || [],
    isLoading,
    error,
    refresh: refetch,
  };
};
